import { CartLinesUpdateEvent } from '@shopify/events';

// Keep the carousel circular without duplicating images or form controls.
export function lookDistance(index, active, count) {
  const forward = (index - active + count) % count;
  return forward > count / 2 ? forward - count : forward;
}

// Elliptical orbit: flat cutouts follow a circular depth path, with a shared floor.
export function lookOrbit(index, turn, count, mobile = false, blur = 0.5, orbitDepth = 80) {
  const angle = (index - turn) * (Math.PI * 2 / count);
  const depth = Math.cos(angle);
  const fade = Math.max(0, Math.min(1, (depth + 0.35) / 0.35));
  const scale = mobile ? -0.14 + 1.10 * depth : 0.84 + 0.16 * depth;
  const orbitX = Math.sin(angle) * (0.4 + 0.6 * Math.abs(Math.sin(angle))) * (mobile ? 68 : 38);
  // Keep the entire model box inside the stage, including during a turn.
  const safeX = 48 - (mobile ? 24 : 12) * scale;
  return {
    x: Math.max(-safeX, Math.min(safeX, orbitX)),
    y: -orbitDepth * (1 - depth),
    scale,
    opacity: fade * (0.9 + 0.1 * Math.max(0, depth)) * (mobile ? Math.max(0, Math.min(1, (depth - 0.25) / 0.35)) : 1),
    blur: blur * (1 - Math.max(0, depth)),
    depth, z: Math.round(100 + depth * 100),
  };
}

export function collectLookItems(selects) {
  if (!selects.length) throw new Error('Choose the products for this look first.');
  const items = new Map();
  for (const select of selects) {
    const option = select.selectedOptions[0];
    const id = select.value;
    if (select.disabled || !id || !/^\d+$/.test(id) || !option || option.disabled) {
      throw new Error('Please choose an available colour and size for every piece.');
    }
    items.set(id, (items.get(id) || 0) + 1);
  }
  return Array.from(items, ([id, quantity]) => ({ id: Number(id), quantity }));
}

class LaasaShopLook extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.models = Array.from(this.querySelectorAll('[data-look-index]'));
    this.forms = Array.from(this.querySelectorAll('[data-look-form]'));
    this.active = 0;
    this.turn = 0;
    this.targetTurn = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.busy = false;
    this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency', currency: this.dataset.currency || 'INR',
    });
    this.addEventListener('click', event => {
      const target = event.target.closest('button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-look-prev')) this.show(this.active - 1);
      if (target.hasAttribute('data-look-next')) this.show(this.active + 1);
      if (target.hasAttribute('data-look-goto')) this.show(Number(target.dataset.lookGoto));
      if (target.hasAttribute('data-look-index')) this.show(Number(target.dataset.lookIndex));
    }, options);
    const stage = this.querySelector('[data-look-stage]');
    stage?.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.active + (event.key === 'ArrowRight' ? 1 : -1));
        this.models[this.active]?.focus({ preventScroll: true });
      }
    }, options);
    stage?.addEventListener('pointerdown', event => {
      if (this.busy) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, turn: this.turn, moving: false };
    }, options);
    stage?.addEventListener('pointermove', event => {
      if (!this.pointer || this.pointer.id !== event.pointerId || this.busy) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.pointer.moving = true;
        stage.setPointerCapture(event.pointerId);
        cancelAnimationFrame(this.animationFrame);
        this.turn = this.pointer.turn - dx / Math.max(80, stage.clientWidth * 0.3);
        this.drawOrbit();
      }
    }, options);
    stage?.addEventListener('wheel', event => {
      if (event.ctrlKey || this.busy || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const now = performance.now();
      if (now < (this.wheelCooldown || 0)) return;
      if (now - (this.lastWheel || 0) > 200) this.wheelDelta = 0;
      this.lastWheel = now;
      this.wheelDelta = (this.wheelDelta || 0) + event.deltaX;
      if (Math.abs(this.wheelDelta) > 35) {
        this.show(this.active + (this.wheelDelta > 0 ? 1 : -1));
        this.wheelDelta = 0;
        this.wheelCooldown = now + 450;
      }
    }, { ...options, passive: false });
    stage?.addEventListener('pointerup', event => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      const moved = this.pointer.moving;
      this.pointer = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.suppressClickUntil = performance.now() + 400;
        this.show(this.active + (dx < 0 ? 1 : -1));
      } else if (moved) {
        this.suppressClickUntil = performance.now() + 400;
        this.show(this.active);
      }
    }, options);
    stage?.addEventListener('pointercancel', () => { this.pointer = null; this.show(this.active); }, options);
    stage?.addEventListener('click', event => {
      if (performance.now() < (this.suppressClickUntil || 0)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, { ...options, capture: true });
    this.addEventListener('change', event => {
      const select = event.target.closest('[data-look-variant]');
      if (select) this.updateForm(select.closest('form'));
    }, options);
    this.addEventListener('submit', event => {
      if (!event.target.matches('[data-look-form]')) return;
      event.preventDefault();
      this.addLook(event.target);
    }, options);
    document.addEventListener('shopify:block:select', event => {
      const model = event.target.closest?.('[data-look-index]');
      if (model && this.contains(model)) this.show(Number(model.dataset.lookIndex));
    }, options);
    this.mobileQuery.addEventListener('change', () => { this.updateDimensions(); this.drawOrbit(); }, options);
    this.updateDimensions();
    this.reducedMotion.addEventListener('change', () => this.show(this.active, true), options);
    this.querySelectorAll('.laasa-look__cutout img').forEach(image => {
      const prepare = () => this.fitCutout(image);
      image.addEventListener('load', prepare, options);
      if (image.complete && image.naturalWidth) prepare();
    });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateDimensions();
        this.querySelectorAll('.laasa-look__cutout img').forEach(image => this.fitCutout(image));
      });
      if (stage) this.resizeObserver.observe(stage);
    }
    this.forms.forEach(form => this.updateForm(form));
    this.show(0, true);
  }

  disconnectedCallback() {
    this.controller?.abort();
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver?.disconnect();
  }

  updateDimensions() {
    const height = this.querySelector('[data-look-stage]')?.clientHeight || 420;
    const mobile = this.mobileQuery.matches;
    const depth = Number(mobile ? this.dataset.mobileOrbitDepth || 45 : this.dataset.orbitDepth || 80);
    const rearDepth = mobile ? 0.25 : -0.35;
    const rearScale = mobile ? -0.14 + 1.10 * rearDepth : 0.84 + 0.16 * rearDepth;
    const bodyLimit = (height - 24 - depth * (1 - rearDepth) - 12) / rearScale;
    const bodyHeight = Math.max(80, Math.min(height - 24 - (mobile ? 26 : 34), bodyLimit, (height - 36) / (mobile ? 1.04 : 1)));
    this.style.setProperty('--look-headroom', `${Math.ceil(height - 24 - bodyHeight)}px`);
  }

  // Normalize transparent PNG margins in the display only; source images stay intact.
  fitCutout(image) {
    if (!image.naturalWidth || !image.parentElement?.clientHeight) return;
    try {
      let bounds = this.cutoutBounds?.get(image);
      if (!bounds || bounds.source !== (image.currentSrc || image.src) || bounds.naturalWidth !== image.naturalWidth) {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(1, 400 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let left = canvas.width, top = canvas.height, right = -1, bottom = -1;
        for (let y = 0; y < canvas.height; y++) for (let x = 0; x < canvas.width; x++) {
          if (pixels[(y * canvas.width + x) * 4 + 3] > 32) {
            left = Math.min(left, x); right = Math.max(right, x);
            top = Math.min(top, y); bottom = Math.max(bottom, y);
          }
        }
        if (right < left || bottom < top) return;
        bounds = { source: image.currentSrc || image.src, naturalWidth: image.naturalWidth, x: left / ratio, y: top / ratio, w: (right - left + 1) / ratio, h: (bottom - top + 1) / ratio };
        this.cutoutBounds ||= new WeakMap();
        this.cutoutBounds.set(image, bounds);
      }
      const box = image.parentElement;
      const scale = Math.min(box.clientWidth / bounds.w, box.clientHeight / bounds.h);
      image.style.width = `${image.naturalWidth * scale}px`;
      image.style.height = `${image.naturalHeight * scale}px`;
      image.style.left = `${(box.clientWidth - bounds.w * scale) / 2 - bounds.x * scale}px`;
      image.style.top = `${box.clientHeight - bounds.h * scale - bounds.y * scale}px`;
      image.classList.add('is-normalized');
    } catch {
      // When a remote image disallows canvas access, the regular contain fit remains usable.
    }
  }

  drawOrbit() {
    const mobile = this.mobileQuery.matches;
    const blur = Number(this.dataset.sideBlur || 0.5);
    const orbitDepth = Number(mobile ? this.dataset.mobileOrbitDepth || 45 : this.dataset.orbitDepth || 80);
    this.models.forEach((model, i) => {
      const frame = lookOrbit(i, this.turn, this.models.length, mobile, blur, orbitDepth);
      model.style.left = `${50 + frame.x}%`;
      model.style.transform = `translate3d(-50%, ${frame.y}px, 0) scale(${frame.scale})`;
      model.style.opacity = String(frame.opacity);
      model.style.filter = `blur(${frame.blur}px)`;
      model.style.zIndex = String(frame.z);
      model.style.visibility = frame.opacity > 0.01 ? 'visible' : 'hidden';
      model.style.pointerEvents = frame.opacity > 0.15 ? 'auto' : 'none';
      model.tabIndex = frame.depth >= -0.01 ? 0 : -1;
      model.setAttribute('aria-hidden', String(frame.opacity <= 0.01));
    });
  }

  show(index, instant = false) {
    if (!this.models.length || this.busy) return;
    const next = (index + this.models.length) % this.models.length;
    const directStep = index - this.active;
    const delta = Math.abs(directStep) === 1 ? directStep : lookDistance(next, this.active, this.models.length);
    this.targetTurn += delta;
    this.active = next;
    this.models.forEach((model, i) => {
      model.dataset.position = i === this.active ? '0' : 'orbit';
      model.setAttribute('aria-pressed', String(i === this.active));
    });
    this.forms.forEach((form, i) => { form.hidden = i !== this.active; });
    this.querySelectorAll('[data-look-goto]').forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.active)));
    const counter = this.querySelector('[data-look-counter]');
    if (counter) counter.textContent = `${this.active + 1} / ${this.models.length}`;
    cancelAnimationFrame(this.animationFrame);
    if (instant || this.reducedMotion.matches) {
      this.turn = this.targetTurn;
      this.drawOrbit();
      return;
    }
    const from = this.turn, to = this.targetTurn;
    const started = performance.now();
    const duration = Number(this.dataset.animationSpeed || 550);
    const tick = now => {
      const progress = Math.min(1, Math.max(0, (now - started) / duration));
      const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - ((-2 * progress + 2) ** 3) / 2;
      this.turn = from + (to - from) * eased;
      this.drawOrbit();
      if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
    };
    this.animationFrame = requestAnimationFrame(tick);
  }

  updateForm(form) {
    const selects = Array.from(form.querySelectorAll('[data-look-variant]'));
    let total = 0;
    let ready = selects.length > 0;
    for (const select of selects) {
      const option = select.selectedOptions[0];
      if (select.disabled || !select.value || !option || option.disabled) { ready = false; continue; }
      total += Number(option.dataset.price || 0);
      const row = select.closest('.laasa-look__product');
      const price = row?.querySelector('[data-look-price]');
      if (price) price.textContent = option.dataset.money || this.formatter.format(Number(option.dataset.price) / 100);
      const image = row?.querySelector('img');
      const url = option.dataset.image || select.dataset.defaultImage;
      if (image && url) { image.removeAttribute('srcset'); image.src = url; }
    }
    const totalNode = form.querySelector('[data-look-total]');
    if (totalNode) totalNode.textContent = ready ? this.formatter.format(total / 100) : 'Choose all options';
    const button = form.querySelector('[data-look-add]');
    if (button) button.disabled = !ready || this.busy;
  }

  async syncCart(sections, didError, items) {
    const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Could not refresh your bag.');
    const cart = await response.json();
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: didError ? 'update' : 'add', context: 'product',
      lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, didError, source: 'laasa-shop-look', sourceId: this.id, itemCount: items.reduce((n, item) => n + item.quantity, 0) },
    });
  }

  async addLook(form) {
    if (this.busy) return;
    const status = form.querySelector('[data-look-status]');
    let items;
    try { items = collectLookItems(Array.from(form.querySelectorAll('[data-look-variant]'))); }
    catch (error) { status.textContent = error.message; form.reportValidity(); return; }
    const button = form.querySelector('[data-look-add]');
    this.busy = true;
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Adding…';
    status.textContent = '';
    let confirmedAdded = false;
    try {
      const sections = [...new Set(Array.from(document.querySelectorAll('cart-items-component'), item => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        const message = typeof result.description === 'string' ? result.description : result.message;
        await this.syncCart(result.sections, true, items).catch(() => {});
        throw new Error(message || 'One of these options is no longer available. Please choose another option.');
      }
      confirmedAdded = true;
      // Cosmetic only: never let animation change the confirmed cart result.
      try {
        const image = form.querySelector('.laasa-look__thumb img');
        const cartIcon = document.querySelector('.header-actions__cart-icon');
        if (image && cartIcon && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const fly = document.createElement('fly-to-cart');
          fly.classList.add('fly-to-cart--quick');
          fly.style.backgroundImage = `url(${JSON.stringify(image.currentSrc || image.src)})`;
          fly.source = button;
          fly.destination = cartIcon;
          document.body.appendChild(fly);
        }
      } catch { /* Adding to the bag remains successful without the visual effect. */ }
      await this.syncCart(result.sections, false, items);
      status.textContent = 'Your look has been added to the bag.';
    } catch (error) {
      status.textContent = confirmedAdded
        ? 'Your look was added. Open your bag to see it; please do not add it again.'
        : (error instanceof TypeError || error instanceof SyntaxError)
          ? 'We could not confirm the update. Check your bag before trying again.'
          : error.message;
      if (confirmedAdded) {
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl.replace(/\.js$/, '');
        link.textContent = ' View bag';
        status.append(link);
      }
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      button.textContent = originalLabel;
      this.updateForm(form);
    }
  }
}

if (!customElements.get('laasa-shop-look')) customElements.define('laasa-shop-look', LaasaShopLook);
